import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Paper,
  Stack,
  Link,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Fade,
  Slide,
} from "@mui/material";
import SEOHead from "../components/SEOHead";
import CitabilitySection from "../components/CitabilitySection";
import {
  WhatsApp as WhatsAppIcon,
  Instagram as InstagramIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import axios from "axios";
import { API_URL } from "../config";

const Contact: React.FC = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/api/contact/submit`, form, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      setSubmitted(true);

      // Reset form
      setForm({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || "Failed to submit contact form"
        );
      } else {
        setError("An unexpected error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <LocationIcon sx={{ fontSize: 24, color: "primary.main" }} />,
      title: "地址",
      content:
        " Room 8,Flat A2, 17/F, Galaxy Factory Building, 25-27 Luk Hop Street, San Po Kong, Kowloon",
      link: null,
    },
    {
      icon: <WhatsAppIcon sx={{ fontSize: 24, color: "#25D366" }} />,
      title: "WhatsApp",
      content: "84378839",
      link: "https://wa.me/85284378839",
    },
    {
      icon: <EmailIcon sx={{ fontSize: 24, color: "primary.main" }} />,
      title: "Email",
      content: "cosylabhk@gmail.com",
      link: "mailto:cosylabhk@gmail.com",
    },
    {
      icon: <InstagramIcon sx={{ fontSize: 24, color: "#E4405F" }} />,
      title: "Instagram",
      content: "@cosylab.craft",
      link: "https://www.instagram.com/cosylab.craft/",
    },
  ];

  return (
    <Box sx={{ 
      minHeight: "100vh",
      background: `
        radial-gradient(ellipse at top left, rgba(139, 195, 74, 0.08) 0%, transparent 50%),
        radial-gradient(ellipse at bottom right, rgba(104, 159, 56, 0.06) 0%, transparent 50%),
        linear-gradient(180deg, rgba(250, 251, 248, 1) 0%, rgba(245, 248, 242, 1) 100%)
      `,
      position: "relative",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(139, 195, 74, 0.02) 35px, rgba(139, 195, 74, 0.02) 70px),
          repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(104, 159, 56, 0.02) 35px, rgba(104, 159, 56, 0.02) 70px)
        `,
        pointerEvents: "none",
        zIndex: 0,
      }
    }}>
      <Container maxWidth="lg" sx={{ 
        py: { xs: 4, md: 8 },
        position: "relative",
        zIndex: 1,
      }}>
        <SEOHead
          title="聯絡我們 - CosyLab"
          description="查詢CosyLab手工藝課程與工作坊，透過WhatsApp、Email或表格聯絡我們。"
          canonical="https://cosylabdesigns.com/contact"
        />
        {/* Header Section */}
        <Slide direction="down" in={true} timeout={800}>
          <Box textAlign="center" sx={{ mb: { xs: 4, md: 6 } }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 50%, #689F38 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textShadow: "0 2px 4px rgba(139, 195, 74, 0.1)",
              }}
            >
              聯絡我們
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "text.secondary",
                maxWidth: 600,
                mx: "auto",
                lineHeight: 1.6,
                fontSize: { xs: "1rem", md: "1.1rem" },
              }}
            >
              想預約工作坊或了解更多課程資訊？歡迎隨時與我們聯繫！
            </Typography>
          </Box>
        </Slide>

      <Slide direction="up" in={true} timeout={1000}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            gap: { xs: 4, md: 6 },
          }}
        >
          {/* Contact Information */}
          <Fade in={true} timeout={1200}>
            <Box>
              <Paper
                sx={{
                  p: { xs: 3, md: 4 },
                  height: "fit-content",
                  borderRadius: 3,
                  background: `
                    linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 251, 248, 0.95) 100%)
                  `,
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(139, 195, 74, 0.1)",
                  boxShadow: "0 8px 32px rgba(139, 195, 74, 0.08)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 40px rgba(139, 195, 74, 0.12)",
                  },
                }}
              >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                聯絡資料
              </Typography>

            <Stack spacing={3}>
              {contactInfo.map((info, index) => (
                <Box key={index}>
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <Box sx={{ mt: 0.5 }}>{info.icon}</Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          fontSize: "1.1rem",
                        }}
                      >
                        {info.title}
                      </Typography>
                      {info.link ? (
                        <Link
                          href={info.link}
                          target={
                            info.link.startsWith("http") ? "_blank" : undefined
                          }
                          rel={
                            info.link.startsWith("http")
                              ? "noopener noreferrer"
                              : undefined
                          }
                          title={`${info.title}: ${info.content}`}
                          underline="hover"
                          sx={{
                            color: "text.primary",
                            fontWeight: 500,
                            fontSize: "1rem",
                            "&:hover": {
                              color: "primary.main",
                            },
                          }}
                        >
                          {info.content}
                        </Link>
                      ) : (
                        <Typography
                          sx={{
                            color: "text.primary",
                            fontWeight: 500,
                            fontSize: "1rem",
                            lineHeight: 1.5,
                          }}
                        >
                          {info.content}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {index < contactInfo.length - 1 && <Divider sx={{ mt: 3 }} />}
                </Box>
              ))}
            </Stack>

            <Box
              sx={{
                mt: 4,
                p: 3,
                background: `
                  linear-gradient(135deg, rgba(139, 195, 74, 0.08) 0%, rgba(104, 159, 56, 0.05) 100%)
                `,
                borderRadius: 2,
                border: "1px solid rgba(139, 195, 74, 0.15)",
              }}
            >
            </Box>
          </Paper>
        </Box>

          {/* Contact Form */}
        </Fade>
        <Fade in={true} timeout={1400}>
          <Box>
            <Paper
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                background: `
                  linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 251, 248, 0.95) 100%)
                `,
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(139, 195, 74, 0.1)",
                boxShadow: "0 8px 32px rgba(139, 195, 74, 0.08)",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 40px rgba(139, 195, 74, 0.12)",
                },
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                聯絡表格
              </Typography>
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  background:
                    "linear-gradient(135deg, rgba(139, 195, 74, 0.08) 0%, rgba(104, 159, 56, 0.05) 100%)",
                  borderRadius: 2,
                  border: "1px solid rgba(139, 195, 74, 0.15)",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#4a6741",
                    lineHeight: 1.7,
                    fontSize: { xs: "0.875rem", md: "0.9rem" },
                  }}
                >
                  建議提供活動日期、預計人數、地點（學校或機構名稱）及課程偏好（如蠟燭製作或植物護理），以便我們更快提供度身訂造方案及報價。
                </Typography>
              </Box>
            {submitted ? (
              <Box textAlign="center" sx={{ py: 4 }}>
                <Alert
                  severity="success"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    "& .MuiAlert-message": {
                      fontSize: "1rem",
                      fontWeight: 500,
                    },
                  }}
                >
                  感謝您的查詢，我們會盡快回覆您！
                </Alert>
                <Button
                  variant="outlined"
                  onClick={() => setSubmitted(false)}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    fontSize: "1rem",
                  }}
                >
                  提交另一個查詢
                </Button>
              </Box>
            ) : (
              <>
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      "& .MuiAlert-message": {
                        fontSize: "1rem",
                        fontWeight: 500,
                      },
                    }}
                  >
                    {error}
                  </Alert>
                )}
                <form onSubmit={handleSubmit}>
                  <Stack spacing={3}>
                    <TextField
                      label="姓名"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      fullWidth
                      disabled={loading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <TextField
                      label="電郵"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      fullWidth
                      disabled={loading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <TextField
                      label="電話"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      fullWidth
                      disabled={loading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <TextField
                      label="查詢內容"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      fullWidth
                      multiline
                      rows={5}
                      disabled={loading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      startIcon={
                        loading ? <CircularProgress size={20} /> : <SendIcon />
                      }
                      sx={{
                        py: 1.5,
                        px: 4,
                        fontSize: "1.1rem",
                        borderRadius: 2,
                        fontWeight: 600,
                        background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
                        boxShadow: "0 4px 12px rgba(139, 195, 74, 0.3)",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          background: "linear-gradient(135deg, #689F38 0%, #8BC34A 100%)",
                          boxShadow: "0 6px 20px rgba(139, 195, 74, 0.4)",
                        },
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    >
                      {loading ? "送出中..." : "送出查詢"}
                    </Button>
                  </Stack>
                </form>
              </>
            )}
          </Paper>
          </Box>
        </Fade>
      </Box>
    </Slide>
    </Container>
    </Box>
  );
};

export default Contact;
